import { useCallback, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { AlertCircle, CheckCircle2, FileText, PencilLine, UploadCloud } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { InlineFieldInput, InlineFieldTextarea } from '@/components/inline-field-group'
import { Label } from '@/components/ui/label'
import { LoadingButton } from '@/components/loading-button'
import { useCreateDropshipQuoteDraft, useCreateScratchDropshipQuoteDraft } from '@/api/hooks'
import {
  useCreateDropshipV2Draft,
  useCreateDropshipV2ScratchDraft,
  useDropshipV2Capabilities,
} from '@/api/dropship-v2'
import { cn } from '@/lib/utils'
import { getApiErrorMessage } from '@/lib/api-error'

const MAX_UPLOAD_MB = 20
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024

function isSupportedQuoteFile(file: File): boolean {
  const lowerName = file.name.toLowerCase()
  return lowerName.endsWith('.aqproj')
}

function validateQuoteFile(file: File): string | null {
  if (!isSupportedQuoteFile(file)) {
    return 'Only AutoQuotes .aqproj project exports are accepted.'
  }
  if (file.size <= 0) {
    return 'Choose a non-empty AutoQuotes .aqproj project export before uploading.'
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return `This quote is over the ${MAX_UPLOAD_MB} MB upload limit.`
  }
  return null
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message
  if (error && typeof error === 'object' && 'detail' in error) {
    const detail = (error as { detail?: unknown }).detail
    if (typeof detail === 'string' && detail.trim()) return detail
  }
  return fallback
}

interface QuoteUploadDropzoneProps {
  file: File | null
  error: string | null
  disabled?: boolean
  onFileChange: (file: File | null) => void
  onError: (message: string | null) => void
}

export function QuoteUploadDropzone({
  file,
  error,
  disabled,
  onFileChange,
  onError,
}: QuoteUploadDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  const selectFile = useCallback(
    (candidate: File | null) => {
      if (!candidate) return
      const validationError = validateQuoteFile(candidate)
      if (validationError) {
        onFileChange(null)
        onError(validationError)
        return
      }
      onFileChange(candidate)
      onError(null)
    },
    [onError, onFileChange],
  )

  const openPicker = () => {
    if (!disabled) inputRef.current?.click()
  }

  return (
    <div className="space-y-3">
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onClick={openPicker}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            openPicker()
          }
        }}
        onDragOver={(event) => {
          event.preventDefault()
          if (!disabled) setIsDragging(true)
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(event) => {
          event.preventDefault()
          setIsDragging(false)
          if (disabled) return
          selectFile(event.dataTransfer.files.item(0))
        }}
        className={cn(
          'flex cursor-pointer flex-col items-center gap-3 rounded-lg border-2 border-dashed px-6 py-7 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/x-aqproj,application/vnd.autoquotes.project,application/octet-stream,.aqproj"
          className="hidden"
          disabled={disabled}
          onChange={(event) => selectFile(event.target.files?.item(0) ?? null)}
        />
        <div className="rounded-full bg-muted p-3">
          <UploadCloud className="h-6 w-6 text-muted-foreground" />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium">Drop an AutoQuotes project export here or click to choose</p>
          <p className="text-xs text-muted-foreground">
            .aqproj only · one quote per draft · max {MAX_UPLOAD_MB} MB
          </p>
        </div>
      </div>

      {file && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="flex items-start gap-3 p-3">
            <FileText className="mt-0.5 h-4 w-4 text-amber-700" />
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="truncate text-sm font-medium">{file.name}</p>
                <Badge variant="outline" className="border-amber-200 bg-white text-xs text-amber-700">
                  Dashboard quote upload
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                Document selected. Upload will create a dropship draft and save audit details internally.
              </p>
            </div>
            <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
          </CardContent>
        </Card>
      )}

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  )
}

interface DropshipQuoteUploadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DropshipQuoteUploadDialog({ open, onOpenChange }: DropshipQuoteUploadDialogProps) {
  const navigate = useNavigate()
  const createDraft = useCreateDropshipQuoteDraft()
  const createScratchDraft = useCreateScratchDropshipQuoteDraft()
  const capabilities = useDropshipV2Capabilities()
  const createV2Draft = useCreateDropshipV2Draft()
  const createV2ScratchDraft = useCreateDropshipV2ScratchDraft()
  const [file, setFile] = useState<File | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [scratchName, setScratchName] = useState('')
  const [scratchNotes, setScratchNotes] = useState('')
  const [scratchNameError, setScratchNameError] = useState<string | null>(null)
  const [scratchSubmitError, setScratchSubmitError] = useState<string | null>(null)

  const useV2 = capabilities.data?.intake_enabled === true
  const mutationPending =
    capabilities.isPending ||
    createDraft.isPending ||
    createScratchDraft.isPending ||
    createV2Draft.isPending ||
    createV2ScratchDraft.isPending

  const reset = () => {
    setFile(null)
    setUploadError(null)
    setScratchName('')
    setScratchNotes('')
    setScratchNameError(null)
    setScratchSubmitError(null)
  }

  const closeAndNavigateToDraft = (draftUuid: string) => {
    reset()
    onOpenChange(false)
    navigate({
      to: '/projects/dropship-drafts/$draftUuid',
      params: { draftUuid },
    })
  }

  const uploadQuote = () => {
    if (!file) {
      setUploadError('Choose an AutoQuotes .aqproj project export before uploading.')
      return
    }
    const validationError = validateQuoteFile(file)
    if (validationError) {
      setUploadError(validationError)
      return
    }

    const mutation = useV2 ? createV2Draft : createDraft
    mutation.mutate(
      { file },
      {
        onSuccess: (draft) => {
          toast.success('Dropship quote draft created')
          closeAndNavigateToDraft(draft.uuid)
        },
        onError: (err) => {
          setUploadError(getApiErrorMessage(err, 'Failed to upload quote'))
          toast.error('Quote upload failed')
        },
      },
    )
  }

  const startScratchDraft = () => {
    const trimmedName = scratchName.trim()
    const trimmedNotes = scratchNotes.trim()
    setScratchSubmitError(null)

    if (!trimmedName) {
      setScratchNameError('Project/job name is required.')
      return
    }

    setScratchNameError(null)
    const callbacks = {
      onSuccess: (draft: { uuid: string }) => {
        toast.success('Scratch dropship draft created')
        closeAndNavigateToDraft(draft.uuid)
      },
      onError: (err: unknown) => {
        setScratchSubmitError(errorMessage(err, 'Scratch draft could not be created. Try again.'))
        toast.error('Scratch draft creation failed')
      },
    }
    if (useV2) {
      createV2ScratchDraft.mutate(
        { projectName: trimmedName, draftNotes: trimmedNotes || undefined },
        callbacks,
      )
    } else {
      createScratchDraft.mutate(
        {
            project_name: trimmedName,
            project_type: 'dropship',
            draft_notes: trimmedNotes || undefined,
          },
        callbacks,
      )
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !mutationPending) reset()
        onOpenChange(nextOpen)
      }}
    >
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Create project</DialogTitle>
          <DialogDescription>
            Start a dropship project from an AutoQuotes .aqproj export, or create a scratch quote draft.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 pt-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <Card className="border-amber-200 bg-amber-50/40 lg:row-span-2">
            <CardContent className="space-y-3 p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-sm font-semibold">AutoQuotes project export</h3>
                    <Badge variant="outline" className="border-amber-200 bg-white text-xs text-amber-700">
                      .aqproj only
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Upload an AutoQuotes .aqproj export, track parsing, then review the draft before any live
                    automation is available. PDF intake is disabled.
                  </p>
                </div>
                <FileText className="h-5 w-5 text-amber-700" />
              </div>

              <QuoteUploadDropzone
                file={file}
                error={uploadError}
                disabled={mutationPending}
                onFileChange={setFile}
                onError={setUploadError}
              />

              <LoadingButton
                loading={createDraft.isPending || createV2Draft.isPending}
                loadingText="Uploading..."
                onClick={uploadQuote}
                disabled={!file || mutationPending}
                className="w-full justify-center"
              >
                Upload quote
              </LoadingButton>
            </CardContent>
          </Card>

          <Card className="border-primary/20 bg-card">
            <CardContent className="space-y-4 p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-sm font-semibold">Start from scratch</h3>
                    <Badge variant="secondary" className="text-xs">
                      New
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Create an empty dropship quote draft when there is no source document yet.
                  </p>
                </div>
                <PencilLine className="h-5 w-5 text-primary" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="scratch-project-name">Project/job name</Label>
                <InlineFieldInput
                  id="scratch-project-name"
                  value={scratchName}
                  onChange={(event) => {
                    setScratchName(event.target.value)
                    if (scratchNameError) setScratchNameError(null)
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault()
                      startScratchDraft()
                    }
                  }}
                  aria-invalid={!!scratchNameError}
                  aria-describedby={scratchNameError ? 'scratch-project-name-error' : undefined}
                  placeholder="Test Cafe"
                  disabled={mutationPending}
                />
                {scratchNameError && (
                  <p id="scratch-project-name-error" className="text-sm text-destructive">
                    {scratchNameError}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="scratch-draft-notes">Draft notes (optional)</Label>
                <InlineFieldTextarea
                  id="scratch-draft-notes"
                  value={scratchNotes}
                  onChange={(event) => setScratchNotes(event.target.value)}
                  placeholder="Initial context for the quote draft"
                  disabled={mutationPending}
                  className="min-h-20"
                />
              </div>

              {scratchSubmitError && (
                <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{scratchSubmitError}</span>
                </div>
              )}

              <LoadingButton
                loading={createScratchDraft.isPending || createV2ScratchDraft.isPending}
                loadingText="Creating..."
                onClick={startScratchDraft}
                disabled={mutationPending}
                className="w-full justify-center"
              >
                Start scratch draft
              </LoadingButton>
            </CardContent>
          </Card>

        </div>

        <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          Live purchase, vendor email, Monday, and PO automation stay held until the later confirmation gate is approved.
        </div>
      </DialogContent>
    </Dialog>
  )
}
